import { useCallback, useMemo } from 'react'
import {
  engagementActionButtonsToStripSuggestions,
  orderStripSuggestionItems,
  processSageListMessage,
  shouldUseInlineSuggestions,
  standardizeMessage,
} from '../lib/chatUtils.js'
import { generateUuid } from '../lib/uuid.js'
import { widgetChatDebug } from '../lib/widgetChatDebug.js'
import { testLocalStorage } from '../lib/storage.js'
import {
  MESSAGE_BUFFER_STORAGE_LIMIT,
  publishMessageBuffer,
} from '../lib/messageBufferSync.js'

export { MESSAGE_BUFFER_STORAGE_LIMIT }

/**
 * Mirror the in-memory thread to localStorage and notify sibling widget
 * mounts on the same page (overlay + slide-out share one buffer per chatId).
 */
export function persistMessageBuffer(
  conf,
  allowLocalStorage,
  messages,
  sourceInstanceId = null,
) {
  if (!allowLocalStorage || !conf?.chatId || !Array.isArray(messages)) {
    return
  }
  publishMessageBuffer(conf.chatId, messages, { sourceInstanceId })
}

function hasSuggestions(message) {
  return (
    Array.isArray(message?.suggestions) && message.suggestions.length > 0
  )
}

function isRenderableBotMessage(message) {
  return (
    message?.from === 'chatbot' &&
    message.type !== 'list' &&
    message.type !== 'typing_indicator'
  )
}

function latestVisitorIndexBefore(messages, index) {
  for (let i = Math.min(index - 1, messages.length - 1); i >= 0; i -= 1) {
    if (messages[i]?.from === 'visitor') return i
  }
  return -1
}

export function attachInlineSuggestionsToCurrentTurn(
  messages,
  incoming,
  suggestions,
  conf,
  layoutType,
) {
  if (
    !shouldUseInlineSuggestions(conf, layoutType) ||
    !Array.isArray(messages) ||
    messages.length === 0
  ) {
    return messages
  }

  const incomingIndex = messages.findIndex((m) => m.id === incoming?.id)
  if (incomingIndex === -1) return messages

  if (incoming?.type === 'list' && suggestions.length > 0) {
    const latestVisitorIndex = latestVisitorIndexBefore(messages, incomingIndex)
    for (let i = incomingIndex - 1; i > latestVisitorIndex; i -= 1) {
      if (!isRenderableBotMessage(messages[i])) continue
      const next = messages.slice()
      next[i] = { ...next[i], suggestions }
      return next
    }
    // List-only turn (common for BotMan ecommerce prelude): Web AI hides
    // `type: list` in MessageList, and inline layouts have no panel strip.
    // Promote the list into a text host so suggestions remain visible.
    const next = messages.slice()
    const listMsg = messages[incomingIndex]
    next[incomingIndex] = {
      ...listMsg,
      type: 'text',
      text:
        typeof listMsg.text === 'string' && listMsg.text.trim()
          ? listMsg.text
          : '',
      suggestions,
    }
    return next
  }

  if (
    !isRenderableBotMessage(incoming) ||
    hasSuggestions(messages[incomingIndex])
  ) {
    return messages
  }

  const latestVisitorIndex = latestVisitorIndexBefore(messages, incomingIndex)
  for (let i = incomingIndex - 1; i > latestVisitorIndex; i -= 1) {
    if (messages[i]?.type !== 'list' || !hasSuggestions(messages[i])) continue
    const next = messages.slice()
    next[incomingIndex] = {
      ...next[incomingIndex],
      suggestions: messages[i].suggestions,
    }
    return next
  }

  return messages
}

/**
 * Mirrors legacy `useMessageHandling`. Returns a `writeToMessages`
 * callback that:
 *   - standardizes the message (id/time/visible/attachment defaults),
 *   - extracts inline suggestions if Sage mode + list message,
 *   - de-duplicates by `id` (the streaming transport mutates the same
 *     bot message multiple times as tokens arrive),
 *   - mirrors the message buffer to localStorage so reloads restore
 *     conversation context.
 */
export function useMessageHandling({
  conf,
  setMessages,
  layoutType,
  onSuggestionsUpdate,
  instanceId = null,
}) {
  const allowLocalStorage = useMemo(() => testLocalStorage(), [])

  const writeToMessages = useCallback(
    (msg) => {
      const standardized = standardizeMessage(msg)
      let suggestions = []
      if (
        Array.isArray(standardized.suggestions) &&
        standardized.suggestions.length > 0
      ) {
        suggestions = standardized.suggestions
      } else {
        suggestions = processSageListMessage(standardized, conf)
      }
      const engagementStrip = engagementActionButtonsToStripSuggestions(
        standardized.buttons,
        conf,
        conf?.activeLeadForms || conf?.leadForms || [],
      )
      if (engagementStrip.length > 0) {
        suggestions = orderStripSuggestionItems([
          ...suggestions,
          ...engagementStrip,
        ])
      }
      if (suggestions.length > 0) standardized.suggestions = suggestions
      if (suggestions.length > 0) onSuggestionsUpdate?.(suggestions)

      setMessages((prev) => {
        let standardizedOut = standardized
        let idx = prev.findIndex((m) => m.id === standardizedOut.id)
        // Never merge a new BotMan payload into an in-flight streaming row;
        // streaming text is owned exclusively by onToken.
        if (
          idx !== -1 &&
          standardizedOut.from === 'chatbot' &&
          prev[idx].from === 'chatbot' &&
          prev[idx].streaming
        ) {
          standardizedOut = { ...standardizedOut, id: generateUuid() }
          idx = -1
        }
        if (
          idx !== -1 &&
          standardizedOut.from === 'chatbot' &&
          prev[idx].from === 'chatbot' &&
          !prev[idx].streaming &&
          !standardizedOut.streaming
        ) {
          const a = prev[idx]
          const b = standardizedOut
          const sameBubble =
            a.type === b.type &&
            (a.type !== 'text' || (a.text || '') === (b.text || ''))
          if (!sameBubble) {
            standardizedOut = { ...standardizedOut, id: generateUuid() }
            idx = -1
          }
        }
        let next
        if (idx !== -1) {
          next = prev.slice()
          next[idx] = { ...next[idx], ...standardizedOut }
        } else {
          next = [...prev, standardizedOut]
        }

        next = attachInlineSuggestionsToCurrentTurn(
          next,
          standardizedOut,
          suggestions,
          conf,
          layoutType,
        )

        persistMessageBuffer(conf, allowLocalStorage, next, instanceId)
        widgetChatDebug('useMessageHandling:commit', {
          action: idx !== -1 ? 'merge_by_id' : 'append',
          chatId: conf?.chatId,
          prevLen: prev.length,
          nextLen: next.length,
          standardizedId: standardizedOut.id,
          from: standardizedOut.from,
          streaming: Boolean(standardizedOut.streaming),
          type: standardizedOut.type,
        })
        return next
      })
    },
    [conf, setMessages, layoutType, onSuggestionsUpdate, allowLocalStorage],
  )

  const persistMessages = useCallback(
    (messages) =>
      persistMessageBuffer(conf, allowLocalStorage, messages, instanceId),
    [conf, allowLocalStorage, instanceId],
  )

  return { writeToMessages, allowLocalStorage, persistMessages }
}
